import { type PropsWithChildren, createContext, useContext } from 'react';

import { useAccessToken } from 'reservations/account/providers/AccessTokenProvider';
import ReservationNotFoundError from 'reservations/base/components/ReservationNotFoundError';
import { useBookingQuery } from 'reservations/booking/queries/useBookingQuery';
import type { BookingResponse } from 'reservations/booking/types';

const BookingContext = createContext<BookingResponse | null>(null);

type BookingProviderProps = {
  bookingId: string;
};

export const BookingProvider = ({
  bookingId,
  children
}: PropsWithChildren<BookingProviderProps>) => {
  const { accessToken, slug } = useAccessToken();
  const { data, isPending, isError } = useBookingQuery(accessToken, bookingId);

  if (isPending) {
    return <></>;
  }

  if (isError) {
    return <ReservationNotFoundError slug={slug} />;
  }

  return (
    <BookingContext.Provider value={data}>{children}</BookingContext.Provider>
  );
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (context == null) {
    throw new Error('useBooking must be used within BookingProvider');
  }

  const {
    opt_in_checkless_payments: optInChecklessPayments,
    saved_payment_method: savedPaymentMethod,
    tip_percentage: tipPercentage
  } = context.card_on_file ?? {};

  const {
    card_expiration_month: cardExpMonth,
    card_expiration_year: cardExpYear,
    card_last4: cardLast4,
    card_type: cardType
  } = savedPaymentMethod?.payment_method ?? {};

  //Makes sure fee is either a fee object or null and not undefined, which simplifies related logic
  return {
    ...context,
    checklessPayments: { optInChecklessPayments, tipPercentage },
    savedPaymentMethod: {
      hasCardOnFile: !!savedPaymentMethod,
      id: savedPaymentMethod?.id,
      cardExpMonth,
      cardExpYear,
      cardLast4,
      cardType
    },
    fee: context?.fee ?? null
  };
};

export const useBookingDetails = () => useBooking()?.booking;

export const useBookingEmail = () => useBookingDetails()?.email;

export const useIsBookingCanceled = () => {
  const state = useBookingDetails()?.state;
  return state === 'Canceled' || state === 'Noshow';
};

export const useHasBookingStarted = () => {
  const state = useBookingDetails()?.state;
  return state === 'Seated';
};

export const useBookingFee = () => useBooking()?.fee;

export const useBookingHasFee = () => useBookingFee()?.shouldCollect;

export const useBookingChecklessPayment = () => useBooking()?.checklessPayments;

export const useBookingPaymentMethod = () => useBooking()?.savedPaymentMethod;

export const useExternalUrl = () => {
  const externalUrl = useBookingDetails()?.externalReservationUrl;
  const hasExternalUrl = !!externalUrl;
  const redirectToExternalUrl = () => window.open(externalUrl, '_blank');
  return { hasExternalUrl, redirectToExternalUrl };
};
